import React, { useState, useEffect } from 'react';
import axios from 'axios';
import showToast from '../../utils/toastHelper';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [stats, setStats] = useState({ total: 0, verified: 0, unverified: 0, admins: 0, voters: 0 });

  useEffect(() => {
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, filter]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });

      if (filter === 'verified') {
        params.append('isEmailVerified', 'true');
      } else if (filter === 'unverified') {
        params.append('isEmailVerified', 'false');
      } else if (filter === 'admin') {
        params.append('role', 'admin');
      } else if (filter === 'voter') {
        params.append('role', 'voter');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUsers(response.data.data?.users || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.data?.pagination?.total || 0,
        totalPages: response.data.data?.pagination?.totalPages || 0,
      }));

      // Calculate stats
      const allUsers = response.data.data?.users || [];
      setStats({
        total: response.data.data?.pagination?.total || 0,
        verified: allUsers.filter(u => u.isEmailVerified).length,
        unverified: allUsers.filter(u => !u.isEmailVerified).length,
        admins: allUsers.filter(u => u.role === 'admin' || u.role === 'super_admin').length,
        voters: allUsers.filter(u => u.role === 'voter').length,
      });
    } catch (error) {
      console.error('Failed to fetch users:', error);
      showToast('Failed to load users', 'error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId) => {
    if (!window.confirm('Mark this user as verified?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/users/${userId}/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast('User verified successfully', 'success');
      fetchUsers();
    } catch (error) {
      console.error('Failed to verify user:', error);
      showToast(error.response?.data?.error || 'Failed to verify user', 'error');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this user?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/users/${userId}/toggle-active`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showToast(`User ${action}d successfully`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      showToast(error.response?.data?.error || 'Failed to update user status', 'error');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: 'danger',
      admin: 'primary',
      voter: 'secondary',
      candidate: 'info',
    };
    return `badge bg-${badges[role] || 'secondary'}`;
  };

  const filteredUsers = users.filter(u =>
    searchTerm === '' ||
    u.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="mb-4">
        <h3 className="mb-1" style={{ fontWeight: '600' }}>User Management</h3>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Manage user accounts, verification, and permissions
        </p>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card h-100" style={{ border: 'none', borderLeft: '4px solid #3b82f6' }}>
            <div className="card-body">
              <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                Total Users
              </div>
              <div className="h4 mb-0 mt-2" style={{ fontWeight: '700', color: '#3b82f6' }}>
                {stats.total}
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card h-100" style={{ border: 'none', borderLeft: '4px solid #10b981' }}>
            <div className="card-body">
              <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                Verified
              </div>
              <div className="h4 mb-0 mt-2" style={{ fontWeight: '700', color: '#10b981' }}>
                {stats.verified}
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card h-100" style={{ border: 'none', borderLeft: '4px solid #f59e0b' }}>
            <div className="card-body">
              <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                Unverified
              </div>
              <div className="h4 mb-0 mt-2" style={{ fontWeight: '700', color: '#f59e0b' }}>
                {stats.unverified}
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 mb-3">
          <div className="card h-100" style={{ border: 'none', borderLeft: '4px solid #8b5cf6' }}>
            <div className="card-body">
              <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase' }}>
                Admins
              </div>
              <div className="h4 mb-0 mt-2" style={{ fontWeight: '700', color: '#8b5cf6' }}>
                {stats.admins}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'verified' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('verified')}
                >
                  Verified
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'unverified' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('unverified')}
                >
                  Unverified
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'admin' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('admin')}
                >
                  Admins
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${filter === 'voter' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('voter')}
                >
                  Voters
                </button>
              </div>
            </div>
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body">
          {filteredUsers.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-people" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">No users found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Organization</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Verified</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                          {user.fullName || 'N/A'}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {user.email}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.875rem' }}>
                        {user.organization?.name || '-'}
                      </td>
                      <td>
                        <span className={getRoleBadge(user.role)} style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                          {user.role?.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        {user.isActive ? (
                          <span className="badge bg-success" style={{ fontSize: '0.75rem' }}>Active</span>
                        ) : (
                          <span className="badge bg-secondary" style={{ fontSize: '0.75rem' }}>Inactive</span>
                        )}
                      </td>
                      <td>
                        {user.isEmailVerified ? (
                          <span className="text-success">
                            <i className="bi bi-check-circle-fill"></i> Verified
                          </span>
                        ) : (
                          <span className="text-warning">
                            <i className="bi bi-exclamation-circle-fill"></i> Unverified
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {formatDate(user.createdAt)}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm" role="group">
                          {!user.isEmailVerified && (
                            <button
                              className="btn btn-outline-success"
                              onClick={() => handleVerifyUser(user.id)}
                              title="Mark as verified"
                            >
                              <i className="bi bi-check-circle"></i>
                            </button>
                          )}
                          <button
                            className={`btn btn-outline-${user.isActive ? 'secondary' : 'primary'}`}
                            onClick={() => handleToggleActive(user.id, user.isActive)}
                            title={user.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <i className={`bi bi-${user.isActive ? 'x-circle' : 'check-circle'}`}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mt-4">
          <div className="d-flex align-items-center gap-3">
            <div className="text-muted" style={{ fontSize: '0.875rem' }}>
              Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
            </div>
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="pageSize" className="text-muted mb-0" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Per page:</label>
              <select
                id="pageSize"
                className="form-select form-select-sm"
                value={pagination.limit}
                onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                style={{ width: 'auto' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {pagination.totalPages > 1 && (
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                const pageNum = pagination.page <= 3 ? i + 1 : pagination.page - 2 + i;
                if (pageNum > pagination.totalPages) return null;
                return (
                  <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
          )}
        </div>
      )}
    </div>
  );
};

export default UserManagement;
